/**
 * Dashboard Layout Components
 * Provides consistent header and container structure for all dashboard pages
 * Supports: /messages, /profile, /my-listings, /my-events, /favorites, /trade-proposals, /notifications
 */
import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Building2, ExternalLink, BadgeCheck } from 'lucide-react';
import { useLanguage, useAuth } from '../App';
import BannedUserBanner from './BannedUserBanner';

// Page metadata with translations (EN/NL/ES)
export const PAGE_METADATA = {
  favorites: {
    title: { en: 'My Collection', nl: 'Mijn Collectie', es: 'Mi Colección' },
    subtitle: { en: 'Your saved listings and searches', nl: 'Je opgeslagen advertenties en zoekopdrachten', es: 'Tus anuncios y búsquedas guardados' },
    breadcrumb: { en: 'Favorites', nl: 'Favorieten', es: 'Favoritos' }
  },
  messages: {
    title: { en: 'Messages', nl: 'Berichten', es: 'Mensajes' },
    subtitle: { en: 'Chat with buyers and sellers', nl: 'Chat met kopers en verkopers', es: 'Chatea con compradores y vendedores' },
    breadcrumb: { en: 'Messages', nl: 'Berichten', es: 'Mensajes' }
  },
  'trade-proposals': {
    title: { en: 'Trade Hub', nl: 'Ruil Centrum', es: 'Centro de Intercambio' },
    subtitle: { en: 'Review and manage your swap offers', nl: 'Bekijk en beheer je ruilaanbiedingen', es: 'Revisa y gestiona tus ofertas de intercambio' },
    breadcrumb: { en: 'Trade Proposals', nl: 'Ruilvoorstellen', es: 'Propuestas de Intercambio' }
  },
  'my-listings': {
    title: { en: 'My Listings', nl: 'Mijn Advertenties', es: 'Mis Anuncios' },
    subtitle: { en: 'Manage your marketplace presence', nl: 'Beheer je marktplaats aanwezigheid', es: 'Gestiona tu presencia en el mercado' },
    breadcrumb: { en: 'My Listings', nl: 'Mijn Advertenties', es: 'Mis Anuncios' }
  },
  'my-events': {
    title: { en: 'My Events', nl: 'Mijn Evenementen', es: 'Mis Eventos' },
    subtitle: { en: 'Manage your created events', nl: 'Beheer je aangemaakte evenementen', es: 'Gestiona tus eventos creados' },
    breadcrumb: { en: 'My Events', nl: 'Mijn Evenementen', es: 'Mis Eventos' }
  },
  profile: {
    title: { en: 'Profile', nl: 'Profiel', es: 'Perfil' },
    subtitle: { en: 'Manage your account settings', nl: 'Beheer je accountinstellingen', es: 'Gestiona la configuración de tu cuenta' },
    breadcrumb: { en: 'Profile', nl: 'Profiel', es: 'Perfil' }
  },
  notifications: {
    title: { en: 'Notifications', nl: 'Meldingen', es: 'Notificaciones' },
    subtitle: { en: 'Stay updated on your activity', nl: 'Blijf op de hoogte van je activiteit', es: 'Mantente al día de tu actividad' },
    breadcrumb: { en: 'Notifications', nl: 'Meldingen', es: 'Notificaciones' }
  }
};

/**
 * Get translated text from page metadata
 */
export const getPageText = (pageKey, field, language) => {
  const page = PAGE_METADATA[pageKey];
  if (!page || !page[field]) return '';
  return page[field][language] || page[field].en || '';
};

/**
 * PageHeader Component
 * Consistent header structure with back button, breadcrumbs, title, and subtitle
 */
export const PageHeader = ({ 
  pageKey, 
  customTitle, 
  customSubtitle,
  showBackButton = true,
  showBusinessBadge = false,
  showPublicProfileLink = false,
  children 
}) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  
  const title = customTitle || getPageText(pageKey, 'title', language);
  const subtitle = customSubtitle || getPageText(pageKey, 'subtitle', language);
  
  const isBusinessAccount = user?.account_type === 'business';
  
  // Smart back navigation: go back in history, fallback to home if no history
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };
  
  return (
    <div className="mb-6" data-testid={`page-header-${pageKey}`}>
      {/* Top Row: Back Button only (no breadcrumbs on utility pages) - Fixed height wrapper */}
      {showBackButton && (
        <div className="flex items-center h-10 mb-6" data-testid={`${pageKey}-nav-row`}>
          <button
            onClick={handleBack}
            className="flex items-center gap-1.5 px-2 py-1 text-muted-foreground hover:text-primary hover:bg-secondary/50 rounded-lg transition-colors"
            data-testid="page-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </button>
        </div>
      )}
      
      {/* Title + Actions Row: Stacked on mobile, side-by-side on desktop */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        {/* Left: Title & Subtitle */}
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              {title}
            </h1>
            {showBusinessBadge && isBusinessAccount && (
              <span className="px-2.5 py-1 bg-orange-500/15 text-orange-600 dark:text-orange-400 text-xs font-semibold rounded-full flex items-center gap-1.5 border border-orange-500/20">
                <Building2 className="w-3.5 h-3.5" />
                Business
              </span>
            )}
            {showBusinessBadge && isBusinessAccount && user?.is_verified_business && (
              <span className="px-2.5 py-1 bg-green-500/15 text-green-600 dark:text-green-400 text-xs font-semibold rounded-full flex items-center gap-1.5 border border-green-500/20">
                <BadgeCheck className="w-3.5 h-3.5" />
                Verified
              </span>
            )}
          </div>
          {subtitle && (
            <p className="text-sm md:text-base text-muted-foreground mt-1">
              {subtitle}
            </p>
          )}
          {showPublicProfileLink && user && (
            <Link
              to={`/seller/${user._id || user.id}`}
              className="inline-flex items-center gap-1.5 mt-2 text-sm text-primary hover:text-primary/80 transition-colors"
              data-testid="view-public-profile-link"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              {language === 'nl' ? 'Bekijk publiek profiel' : language === 'es' ? 'Ver perfil público' : 'View public profile'}
            </Link>
          )}
        </div>
        
        {/* Right: Action Buttons (children) */}
        {children && (
          <div className="flex items-center gap-2 mt-3 md:mt-0">
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * DashboardContainer Component
 * Unified container wrapper with consistent width and spacing
 * Shows banned user banner if applicable
 */
export const DashboardContainer = ({ children, className = '' }) => {
  const { user } = useAuth();
  
  return (
    <div className={`container mx-auto max-w-6xl px-4 sm:px-6 py-6 ${className}`}>
      {/* Show banned banner at top of every dashboard page */}
      <BannedUserBanner user={user} />
      {children}
    </div>
  );
};

/**
 * FilterChips Component
 * Unified filter chip/pill navigation for dashboard pages
 * @param {Array} options - Array of filter options: { key, label, icon?, count? }
 * @param {string} activeFilter - Currently active filter key
 * @param {function} onFilterChange - Callback when filter changes
 * @param {boolean} showBadges - Whether to show count badges (default: true)
 */
export const FilterChips = ({ options, activeFilter, onFilterChange, showBadges = true }) => {
  return (
    <div className="flex gap-2 mb-8 overflow-x-auto pb-2 scrollbar-hide" data-testid="filter-chips">
      {options.map((opt) => {
        const isActive = activeFilter === opt.key;
        const IconComponent = opt.icon;
        const countIsLoading = opt.count === undefined;
        const countValue = opt.count ?? 0;
        
        return (
          <button
            key={opt.key}
            onClick={() => onFilterChange(opt.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
              isActive 
                ? 'bg-primary text-white shadow-md shadow-primary/25' 
                : 'bg-secondary text-foreground hover:bg-secondary/80'
            }`}
            data-testid={`filter-chip-${opt.key}`}
          >
            {IconComponent && <IconComponent className="w-3.5 h-3.5" />}
            {opt.label}
            {/* Always render badge container to prevent layout shift */}
            {showBadges && (
              <span 
                className={`min-w-[20px] h-[18px] flex items-center justify-center px-1.5 rounded-full text-[10px] ${
                  countIsLoading
                    ? 'bg-muted/30 animate-pulse'
                    : isActive 
                      ? 'bg-white/20' 
                      : 'bg-muted-foreground/20'
                }`}
              >
                {!countIsLoading && countValue}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

/**
 * DashboardPage Component
 * Complete page wrapper with Navbar, Container, and Footer slots
 */
export const DashboardPage = ({ 
  pageKey,
  customTitle,
  customSubtitle,
  showBackButton = true,
  backPath = '/',
  headerChildren,
  children 
}) => {
  return (
    <DashboardContainer>
      <PageHeader 
        pageKey={pageKey}
        customTitle={customTitle}
        customSubtitle={customSubtitle}
        showBackButton={showBackButton}
        backPath={backPath}
      >
        {headerChildren}
      </PageHeader>
      {children}
    </DashboardContainer>
  );
};

export default { PageHeader, DashboardContainer, DashboardPage, FilterChips, PAGE_METADATA, getPageText };
